import React, { useEffect, useState } from 'react';
import {
  BooleanInput,
  FormDataConsumer,
  FormWithRedirect,
  NumberInput,
  ReferenceInput,
  SelectInput,
  minValue,
  required,
  SaveButton,
  useDataProvider,
  useNotify,
  useUpdate,
} from 'react-admin';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { useTranslate } from '../common/language';
import { validateInteger } from './Validator';

const valueOrDefault = (value, defaultValue) =>
  value === null || value === undefined ? defaultValue : value;

const ibanOptionText = record => {
  if (!record) return '';
  const name = record.name || '';
  const iban = record.iban || '';
  return iban ? `${name} — ${iban}` : name;
};

export const SettingsTicketsReservations = () => {
  const translate = useTranslate();
  const history = useHistory();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [update] = useUpdate();
  const [info, setInfo] = useState();
  const associationId = localStorage.getItem('associations_id');
  const integerValidation = [required(), validateInteger(translate('common.validation.number'))];

  useEffect(() => {
    if (associationId > 0) {
      dataProvider.getOne('associations', { id: associationId })
        .then(({ data }) => setInfo(data))
        .catch(error => notify(error.message, 'error'));
    }
  }, [associationId, dataProvider, notify]);

  if (!info) return null;

  const handleSubmit = values => {
    update(
      {
        type: 'update',
        resource: 'associations/tickets',
        payload: {
          id: values.id,
          data: {
            tickets_box_office_sale_enabled: values.tickets_box_office_sale_enabled,
            tickets_reservation_enabled: values.tickets_reservation_enabled,
            tickets_reservation_start_days: values.tickets_reservation_start_days,
            tickets_reservation_end_days: values.tickets_reservation_end_days,
            tickets_reservation_payment_end_days: values.tickets_reservation_payment_end_days,
            tickets_reservation_payment_reminder_hours: values.tickets_reservation_payment_reminder_hours,
            tickets_reservation_auto_cancel: values.tickets_reservation_auto_cancel,
            tickets_reservation_auto_cancel_delay_hours: values.tickets_reservation_auto_cancel_delay_hours,
            tickets_reservation_notify_box_office_users: values.tickets_reservation_notify_box_office_users,
            tickets_reservation_ibans_id: values.tickets_reservation_ibans_id,
          },
        },
      },
      {
        onSuccess: () => history.push('/settings-tickets'),
        onFailure: error => notify(error.message, 'error'),
      }
    );
  };

  return (
    <FormWithRedirect
      resource="associations"
      save={handleSubmit}
      initialValues={{
        id: associationId,
        tickets_box_office_sale_enabled: info.tickets_box_office_sale_enabled !== false,
        tickets_reservation_enabled: info.tickets_reservation_enabled === true,
        tickets_reservation_start_days: valueOrDefault(info.tickets_reservation_start_days, 30),
        tickets_reservation_end_days: valueOrDefault(info.tickets_reservation_end_days, 5),
        tickets_reservation_payment_end_days: valueOrDefault(info.tickets_reservation_payment_end_days, 2),
        tickets_reservation_payment_reminder_hours: valueOrDefault(info.tickets_reservation_payment_reminder_hours, 120),
        tickets_reservation_auto_cancel: info.tickets_reservation_auto_cancel === true,
        tickets_reservation_auto_cancel_delay_hours: valueOrDefault(info.tickets_reservation_auto_cancel_delay_hours, 72),
        tickets_reservation_notify_box_office_users: info.tickets_reservation_notify_box_office_users !== false,
        tickets_reservation_ibans_id: info.tickets_reservation_ibans_id,
      }}
      render={formProps => (
        <>
          <br />
          <Typography variant="h5" className="grey8" align="center">
            {translate('tickets_reservations.title')}
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 850, bgcolor: 'background.paper', padding: '25px' }} className="settings_box">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BooleanInput fullWidth source="tickets_box_office_sale_enabled" label={translate('tickets_reservations.box_office_sale_enabled')} />
              </Grid>
              <Grid item xs={12}>
                <BooleanInput fullWidth source="tickets_reservation_enabled" label={translate('tickets_reservations.enabled')} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6">{translate('tickets_reservations.booking_period')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberInput fullWidth source="tickets_reservation_start_days" label={translate('tickets_reservations.start_days')} validate={integerValidation} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberInput fullWidth source="tickets_reservation_end_days" label={translate('tickets_reservations.end_days')} validate={integerValidation} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6">{translate('tickets_reservations.reservation_payment')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberInput
                  fullWidth
                  source="tickets_reservation_payment_end_days"
                  label={translate('tickets_reservations.payment_deadline_days')}
                  validate={[...integerValidation, minValue(0)]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberInput
                  fullWidth
                  source="tickets_reservation_payment_reminder_hours"
                  label={translate('tickets_reservations.payment_reminder_hours')}
                  validate={[...integerValidation, minValue(0)]}
                />
              </Grid>
              <Grid item xs={12}>
                <BooleanInput
                  fullWidth
                  source="tickets_reservation_auto_cancel"
                  label={translate('tickets_reservations.auto_cancel')}
                />
              </Grid>
              <FormDataConsumer>
                {({ formData }) => formData.tickets_reservation_auto_cancel && (
                  <Grid item xs={12}>
                    <NumberInput
                      fullWidth
                      source="tickets_reservation_auto_cancel_delay_hours"
                      label={translate('tickets_reservations.auto_cancel_delay_hours')}
                      validate={[...integerValidation, minValue(0)]}
                    />
                  </Grid>
                )}
              </FormDataConsumer>
              <Grid item xs={12}>
                <BooleanInput
                  fullWidth
                  source="tickets_reservation_notify_box_office_users"
                  label={translate('tickets_reservations.notify_box_office_users')}
                />
              </Grid>
              <Grid item xs={12}>
                <ReferenceInput
                  fullWidth
                  source="tickets_reservation_ibans_id"
                  reference="ibans"
                  label={translate('tickets_reservations.ibans_id')}
                  filter={{ associations_id: associationId }}
                  perPage={1000}
                  sort={{ field: 'priority', order: 'ASC' }}
                >
                  <SelectInput optionText={ibanOptionText} optionValue="id" fullWidth />
                </ReferenceInput>
              </Grid>

            </Grid>

            <SaveButton saving={formProps.saving} handleSubmitWithRedirect={formProps.handleSubmitWithRedirect} />
          </Box>
        </>
      )}
    />
  );
};
